import { gql, useLazyQuery } from "@apollo/client";
import Button from "components/base/Button";
import { Input, Select } from "components/base/Form";
import Page from "components/base/Page";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { Addresses } from "./Addresses";
import { Fragment, useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { CURRENCIES } from "utils/invoice";
import { download } from "utils/http";
import writtenNumber from "written-number";
import { createShipmentInvoicePoGroups } from "../../utils";

const PRICES = gql`
  query CommercialInvoicePrices($shipmentId: ID!, $priceSource: String!) {
    commercialInvoicePrices(
      shipmentId: $shipmentId
      priceSource: $priceSource
    ) {
      rowId
      unitPrice
      error
    }
  }
`;

const PAYMENT_TERMS = gql`
  query CommercialInvoicePaymentTerms($shipmentId: ID!) {
    shipment(id: $shipmentId) {
      paymentTerms
    }
  }
`;

const PREVIEW = gql`
  query CommercialInvoicePreview($shipmentId: ID!, $data: String!) {
    commercialInvoicePreview(shipmentId: $shipmentId, data: $data)
  }
`;

const calcShipmentBalance = (shipment, deposite = 0) =>
  shipment.shipmentInvoices
    .flatMap((invoice) => invoice.shipmentInvoiceRows)
    .reduce((total, row) => total + row.qty * row.unitPrice, 0) -
  Number(deposite || 0);

const normalizeFobShippingTerm = (shipment) => {
  const loadingPort = (shipment.loadingPort || "").trim();
  if (!shipment.shippingTerm?.trim().startsWith("[FOB]") || !loadingPort) {
    return shipment;
  }
  return { ...shipment, shippingTerm: `[FOB] ${loadingPort}` };
};

const resetPrices = (shipment) => ({
  ...shipment,
  shipmentInvoices: shipment.shipmentInvoices.map((invoice) => ({
    ...invoice,
    shipmentInvoiceRows: invoice.shipmentInvoiceRows.map((row) => ({
      ...row,
      unitPrice: 0,
    })),
  })),
});

function CommercialInvoiceForm({ initialShipment }) {
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(() =>
    resetPrices(normalizeFobShippingTerm(initialShipment)),
  );
  const [currency, setCurrency] = useState(
    CURRENCIES.find((item) => item.code === initialShipment.odooCurrency[0]),
  );
  const [deposite, setDeposite] = useState(0);
  const [priceSource, setPriceSource] = useState("odoo");
  const [pricesReady, setPricesReady] = useState(false);
  const [paymentTermsReady, setPaymentTermsReady] = useState(false);
  const [hasEditedPrices, setHasEditedPrices] = useState(false);
  const [priceErrors, setPriceErrors] = useState({});
  const [generating, setGenerating] = useState("");
  const balance = calcShipmentBalance(shipment, deposite);

  const values = () => ({
    currency: currency?.code,
    deposite: Number(deposite || 0),
    balance,
    paymentTerms: shipment.paymentTerms,
    prices: Object.fromEntries(
      shipment.shipmentInvoices
        .flatMap((invoice) => invoice.shipmentInvoiceRows)
        .map((row) => [row.id, row.unitPrice]),
    ),
  });

  const [loadPrices] = useLazyQuery(PRICES, {
    onCompleted: ({ commercialInvoicePrices }) => {
      const prices = Object.fromEntries(
        commercialInvoicePrices.map((item) => [item.rowId, item.unitPrice]),
      );
      setPriceErrors(
        Object.fromEntries(
          commercialInvoicePrices
            .filter((item) => item.error)
            .map((item) => [item.rowId, item.error]),
        ),
      );
      setShipment((current) => ({
        ...current,
        shipmentInvoices: current.shipmentInvoices.map((invoice) => ({
          ...invoice,
          shipmentInvoiceRows: invoice.shipmentInvoiceRows.map((row) => ({
            ...row,
            unitPrice: prices[row.id] ?? 0,
          })),
        })),
      }));
      setHasEditedPrices(false);
      setPricesReady(true);
    },
    onError: (error) => {
      setPricesReady(true);
      Alert("error", error.message);
    },
  });

  const [loadPreview, { loading: loadingPreview }] = useLazyQuery(PREVIEW);
  const [loadPaymentTerms, { loading: loadingPaymentTerms }] = useLazyQuery(
    PAYMENT_TERMS,
    {
      onCompleted: ({ shipment: paymentTermsShipment }) => {
        setShipment((current) => ({
          ...current,
          paymentTerms: paymentTermsShipment.paymentTerms,
        }));
        setPaymentTermsReady(true);
      },
      onError: (error) => {
        setPaymentTermsReady(true);
        Alert("error", error.message);
      },
    },
  );

  useEffect(() => {
    loadPrices({
      variables: { shipmentId: shipment.id, priceSource: "odoo" },
    });
    loadPaymentTerms({ variables: { shipmentId: shipment.id } });
  }, []);

  const switchSource = (next) => {
    if (next === priceSource) return;
    if (
      hasEditedPrices &&
      !window.confirm("Switching price source will overwrite current prices.")
    ) {
      return;
    }
    setPriceSource(next);
    setPricesReady(false);
    setPriceErrors({});
    setShipment((current) => resetPrices(current));
    loadPrices({ variables: { shipmentId: shipment.id, priceSource: next } });
  };

  const changePrice = (shipmentInvoiceId, rowId, unitPrice) => {
    setHasEditedPrices(true);
    if (unitPrice !== "" && Number(unitPrice) >= 0) {
      setPriceErrors((current) => {
        const { [rowId]: _, ...remaining } = current;
        return remaining;
      });
    }
    setShipment({
      ...shipment,
      shipmentInvoices: shipment.shipmentInvoices.map((invoice) =>
        invoice.id !== shipmentInvoiceId
          ? invoice
          : {
              ...invoice,
              shipmentInvoiceRows: invoice.shipmentInvoiceRows.map((row) =>
                row.id === rowId ? { ...row, unitPrice } : row,
              ),
            },
      ),
    });
  };

  const preview = async () => {
    if (!currency) return Alert("error", "Please set currency.");
    if (Object.keys(priceErrors).length) {
      return Alert("error", "Please enter all missing prices.");
    }
    const data = values();
    try {
      const result = await loadPreview({
        variables: { shipmentId: shipment.id, data: JSON.stringify(data) },
      });
      navigate(`/shipment/doc/commercial-invoice/${shipment.id}/preview`, {
        state: {
          shipment,
          data,
          html: result.data.commercialInvoicePreview,
        },
      });
    } catch (error) {
      Alert("error", error.message);
    }
  };

  const generate = (fileFormat) => {
    if (!currency) return Alert("error", "Please set currency.");
    setGenerating(fileFormat);
    download(
      `/shipment/${shipment.id}/commercial-invoice/${fileFormat}/`,
      `Commercial Invoice #${shipment.invoiceNumber}.${fileFormat}`,
      values(),
    )
      .catch((error) => Alert("error", String(error)))
      .finally(() => setGenerating(""));
  };

  const sayTotal = (value) => {
    const [dollars, cents] = value.toFixed(2).split(".");
    return [
      Number(dollars) ? writtenNumber(dollars) : "",
      Number(cents) ? `AND CENTS ${writtenNumber(cents)}` : "",
    ]
      .filter(Boolean)
      .join(" ");
  };
  const paymentTerms = loadingPaymentTerms ? (
    <span className="inline-flex align-middle ml-1">
      <InlineSpinner size={14} text={null} />
    </span>
  ) : (
    shipment.paymentTerms
  );

  return (
    <Page
      className="lg:h-screen"
      title="Edit Commercial Invoice"
      backTo={`/shipment/shipments/${shipment.id}`}
      rightButtons={
        <div className="flex gap-6">
          <Button
            icon={<FiDownload />}
            loading={generating === "pdf"}
            disabled={
              !pricesReady ||
              !paymentTermsReady ||
              loadingPreview ||
              Object.keys(priceErrors).length ||
              Boolean(generating)
            }
            onClick={() => generate("pdf")}
          >
            Generate PDF
          </Button>
          <Button
            icon={<FiDownload />}
            loading={generating === "xlsx"}
            disabled={
              !pricesReady ||
              !paymentTermsReady ||
              loadingPreview ||
              Object.keys(priceErrors).length ||
              Boolean(generating)
            }
            onClick={() => generate("xlsx")}
          >
            Generate Excel
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 mx-auto max-w-6xl my-8 px-6">
        <div className="flex items-center gap-4 text-sm font-medium">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="price-source"
              checked={priceSource === "odoo"}
              disabled={!pricesReady}
              onChange={() => switchSource("odoo")}
            />
            Odoo Price
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="price-source"
              checked={priceSource === "wis"}
              disabled={!pricesReady}
              onChange={() => switchSource("wis")}
            />
            WIS Cost Price
          </label>
          {!pricesReady && (
            <InlineSpinner
              size={16}
              text={`Loading ${priceSource === "odoo" ? "Odoo" : "WIS cost"} prices...`}
            />
          )}
        </div>
        {Object.entries(priceErrors).map(([rowId, error]) => (
          <div key={rowId} className="text-sm text-red-600">
            {error}
          </div>
        ))}

        <div className="card relative overflow-auto">
          <div className="absolute pin-t pin-l pl-4">
            <img
              className="logo"
              width="180"
              src="https://cdn.waboba.com/public/waboba-logo-w-waboba.png"
              alt="Waboba Group"
            />
          </div>
          <h2 className="py-4 text-center">
            {shipment.customer.system === "AB" ? (
              "WABOBA AB"
            ) : (
              <>
                WABOBA (Guangzhou) Sporting Goods Co., Limited
                <div className="mt-2">万博（广州）运动用品有限公司</div>
              </>
            )}
          </h2>
          <div className="my-6">
            <Addresses system={shipment.customer.system} />
          </div>

          <div className="flex justify-center">
            <h2 className="text-center px-4 pb-2 my-6 border-b">
              Commercial Invoice
            </h2>
          </div>

          <div className="pt-4 flex justify-between">
            <div>
              <div className="mb-4 text-base">
                <b>Inv No.:</b> #{shipment.invoiceNumber}
              </div>
              <div className="leading-normal">
                <b>Consignee</b>
                <div>{shipment.shipmentConsigneeName}</div>
                <div className="whitespace-pre-wrap">{shipment.addr}</div>
                <div className="mt-2">
                  <b>Notify Party: </b>
                  <div>{shipment.shipmentNotifyPartyName}</div>
                  <div className="whitespace-pre-wrap">
                    {shipment.notifyParty}
                  </div>
                </div>
              </div>
            </div>
            <div className="text-right leading-normal">
              <div>
                <b>Invoice Date: </b>
                {new Date().toLocaleDateString()}
              </div>
              {shipment.customer.system === "AB" ? (
                <>
                  <div>
                    <b>Customer PO#: </b>
                    {shipment.po}
                  </div>
                  <div>
                    <b>VV: </b>
                    {shipment.vv}
                  </div>
                  <div>
                    <b>BL Number: </b>
                    {shipment.blNumber}
                  </div>
                  <div>
                    <b>Container Number: </b>
                    {shipment.containerNumber}
                  </div>
                  <div>
                    <b>Shipping Terms: </b>
                    {shipment.shippingTerm}
                  </div>
                  <div>
                    <b>Loading Port: </b>
                    {shipment.loadingPort}
                  </div>
                  <div>
                    <b>Port of Destination: </b>
                    {shipment.portOfDestination}
                  </div>
                  <div>
                    <b>Payment Terms: </b>
                    {paymentTerms}
                  </div>
                  <div>
                    <b>Country of Origin: </b>
                    {shipment.countryOfOrigin}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <b>Customer PO#: </b>
                    {shipment.po}
                  </div>
                  <div>
                    <b>Tracking Number: </b>
                    {shipment.vv}
                  </div>
                  <div>
                    <b>Shipping Terms: </b>
                    {shipment.shippingTerm}
                  </div>
                  <div>
                    <b>Ship To: </b>
                    {shipment.shipTo}
                  </div>
                  <div>
                    <b>Payment Terms: </b>
                    {paymentTerms}
                  </div>
                  <div>
                    <b>Country of Origin: </b>
                    {shipment.countryOfOrigin}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="text-right mt-4">
            <Select
              textAlignLast="center"
              value={currency?.code || ""}
              onChange={(event) =>
                setCurrency(
                  CURRENCIES.find((item) => item.code === event.target.value),
                )
              }
            >
              <option value="" disabled>
                -- Currency --
              </option>
              {CURRENCIES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code} {item.symbol}
                </option>
              ))}
            </Select>
            {initialShipment.odooCurrency.length === 0 && (
              <div className="mt-1 text-red-600 font-semibold">
                No currency found in Odoo order, please manually set currency.
              </div>
            )}
          </div>

          <div className="mt-4">
            <table className="border border-gray-200 dark:border-gray-700 w-full">
              <thead className="uppercase bg-gray-800 text-gray-100">
                <tr>
                  <th align="left" width={120}>
                    ITEM NO
                  </th>
                  <th align="center" width={120}>
                    HS CODE
                  </th>
                  <th align="left">Item Description</th>
                  <th className="text-right" width={100}>
                    Qty
                  </th>
                  <th className="text-right" width={150}>
                    {priceSource === "wis"
                      ? "FIRST SALE PRICE BASIS / PCS"
                      : "PRICE / PCS"}
                  </th>
                  <th className="text-right" width={90}>
                    Total Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {shipment.shipmentInvoices.map((shipmentInvoice) => (
                  <ShipmentInvoice
                    key={shipmentInvoice.id}
                    shipmentInvoice={shipmentInvoice}
                    onChangePrice={changePrice}
                    currency={currency}
                    pricesLoading={!pricesReady}
                  />
                ))}
                <tr>
                  <td className="font-bold" colSpan={5}>
                    Deposite Payment
                  </td>
                  <td align="right">
                    <Input
                      className="text-right"
                      type="number"
                      value={deposite}
                      onChange={(event) => setDeposite(event.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="font-bold" colSpan={3}>
                    Total Balance Amount
                  </td>
                  <td align="right">{shipment.totalQty}</td>
                  <td />
                  <td align="right">
                    {currency?.symbol} {balance.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="uppercase" colSpan={6}>
                    SAY TOTAL {currency?.saying}: {sayTotal(balance)} ONLY.
                  </td>
                </tr>
                {priceSource === "wis" && (
                  <tr>
                    <td colSpan={6}>
                      This invoice reflects the transaction value based on the
                      first sale for exportation to the United States as per 19
                      CFR § 152.103(a)(1). Supporting documentation is available
                      upon request.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <b>Carton Marking</b>
            <div className="mt-4 whitespace-pre-wrap">
              {shipment.shippingMark}
            </div>
          </div>

          <div className="mt-4">
            <b>Company Name and Authorized Signature</b>
            <div className="mt-4">
              <img
                className="inline-block"
                width="160"
                src={
                  shipment.customer.system === "AB"
                    ? "https://cdn.waboba.com/wiswabobacomshipmentstampforinternaluseonly.jpg"
                    : "https://cdn.waboba.com/public/wiswabobacomwabobaltdstamp.png"
                }
                alt="STAMP"
              />
              <div className="mt-4">
                {shipment.customer.system === "AB" ? (
                  "WABOBA AB"
                ) : (
                  <>
                    WABOBA (Guangzhou) Sporting Goods Co., Limited
                    <div className="mt-2">万博（广州）运动用品有限公司</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <Button
          primary
          className="self-start text-sm !px-4 !py-1.5"
          disabled={!pricesReady || !paymentTermsReady || Boolean(generating)}
          loading={loadingPreview}
          onClick={preview}
        >
          Preview
        </Button>
      </div>
    </Page>
  );
}

function ShipmentInvoice({
  shipmentInvoice,
  onChangePrice,
  currency,
  pricesLoading,
}) {
  return createShipmentInvoicePoGroups(shipmentInvoice).map(
    (group, groupIndex) => (
      <Fragment key={groupIndex}>
        <tr className="bg-gray-200 dark:bg-gray-900 font-bold">
          <td colSpan={6}>SO #{shipmentInvoice.invoice.number}</td>
        </tr>
        {group.po && (
          <tr className="bg-gray-100 text-gray-600">
            <td colSpan={6}>PO #{group.po.number}</td>
          </tr>
        )}
        {group.shipmentInvoiceRows.map((row) => (
          <tr key={row.id}>
            <td className="border-b dark:border-gray-700">
              {row.itemNumber}
            </td>
            <td className="border-b dark:border-gray-700">
              {row.hsCodeForDestination}
            </td>
            <td className="border-b dark:border-gray-700 text-xs">
              {row.packingName}
            </td>
            <td className="border-b dark:border-gray-700" align="right">
              {row.qty}
            </td>
            <td className="border-b dark:border-gray-700" align="right">
              <Input
                className="text-right p-1 w-full"
                type="number"
                min={0}
                value={row.unitPrice}
                disabled={pricesLoading}
                onChange={(event) =>
                  onChangePrice(shipmentInvoice.id, row.id, event.target.value)
                }
              />
            </td>
            <td className="border-b dark:border-gray-700" align="right">
              {currency?.symbol} {(row.qty * row.unitPrice).toFixed(2)}
            </td>
          </tr>
        ))}
      </Fragment>
    ),
  );
}

export default CommercialInvoiceForm;
